import { ReactNode, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createTask } from 'api/tasks';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { TaskForm } from 'components/tasks/form/TaskForm';
import { taskFormToRequest } from 'components/tasks/form/upstream';
import { TaskFormValues, useTaskForm } from 'components/tasks/utils/useTaskForm';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Task, TaskStatus } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AddTaskDialogProps {
    children: ReactNode;
    defaultStatus?: TaskStatus;
    onSuccess?: (task: Task) => void;
}
const AddTaskDialog = ({ children, defaultStatus, onSuccess }: AddTaskDialogProps) => {
    const { id: currentUserId } = useCurrentUser();
    const { error } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const { accountId } = useCurrentAccount();
    const { methods } = useTaskForm({
        defaultStatus,
        defaultAssigneeId: currentUserId,
    });
    const { handleSubmit, reset } = methods;

    const { mutate: submit, isPending } = useMutation<Task, GivebutterAxiosError, TaskFormValues>({
        mutationFn: values => {
            return createTask(accountId, taskFormToRequest(values));
        },

        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tasks(accountId) });
            setIsOpen(false);
            onSuccess?.(response);
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to create task. Please try again.');
        },
    });

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={isOpen => {
                setIsOpen(isOpen);
                reset();
            }}
        >
            <Dialog.Trigger asChild>{children}</Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-xl p-0">
                    <Dialog.Title className="sr-only">Add task</Dialog.Title>
                    <Form.Provider {...methods}>
                        <form
                            className="flex flex-col gap-2"
                            onSubmit={handleSubmit(values => submit(values))}
                        >
                            <Dialog.Body className="p-6">
                                <Dialog.Close />
                                <TaskForm inModal />
                            </Dialog.Body>
                            <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
                                <Dialog.Cancel asChild>
                                    <Button variant="outline" color="neutral">
                                        Cancel
                                    </Button>
                                </Dialog.Cancel>
                                <Form.SubmitButton isSubmitting={isPending}>
                                    Create task
                                </Form.SubmitButton>
                            </div>
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export { AddTaskDialog };
export type { AddTaskDialogProps };
